import { Divider, Typography } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import React from "react";

const ITEM_HEIGHT = 58;

export interface CustomCardMenuItem {
  disabled?: boolean;
  label?: React.ReactNode;
  testId?: string;
  onSelect?: () => void;
  isDivider?: boolean;
  isStaticText?: boolean;
}

export interface CustomCardMenuProps {
  className?: string;
  disabled?: boolean;
  menuItems: CustomCardMenuItem[];
  onClose?: () => void;
}

const useStyles = makeStyles(
  theme => ({
    iconButton: {
      background: theme.palette.background.paper,
      borderRadius: "100%",
      height: 32,
      padding: 0,
      width: 32
    },
    menuItem: {
      "&:hover": {
        color: "#0030FF",
        fontWeight: "bold"
      }
    },
    paper: {
      marginTop: theme.spacing(2),
      maxHeight: ITEM_HEIGHT * 4.5
    },
    staticText: {
      alignItems: "center",
      color: "#747C8C",
      fontWeight: "bold",
      letterSpacing: "0.4em",
      paddingBottom: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: 0
    }
  }),
  { name: "CardMenu" }
);

const CustomCardMenu: React.FC<CustomCardMenuProps> = ({
  onClose,
  ...props
}) => {
  const { className, disabled, menuItems, ...rest } = props;
  const classes = useStyles(props);

  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);

  const handleToggle = () => setOpen(prevOpen => !prevOpen);

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  };

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleMenuClick = (index: number) => {
    menuItems[index].onSelect();
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={className} {...rest}>
      <IconButton
        aria-label="More"
        aria-owns={open ? "long-menu" : null}
        aria-haspopup="true"
        className={classes.iconButton}
        color="primary"
        disabled={disabled}
        ref={anchorRef}
        onClick={handleToggle}
      >
        <MoreVertIcon />
      </IconButton>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "right top" : "right bottom"
            }}
          >
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {menuItems.map((menuItem, menuItemIndex) => {
                    if (menuItem.isDivider) {
                      return <Divider key={`divider-${menuItemIndex}`} />;
                    } else if (menuItem.isStaticText) {
                      return (
                        <Typography
                          key={`static-text-${menuItemIndex}`}
                          variant="body2"
                          className={classes.staticText}
                        >
                          {menuItem.label}
                        </Typography>
                      );
                    } else {
                      return (
                        <MenuItem
                          className={classes.menuItem}
                          disabled={menuItem.disabled}
                          onClick={() => handleMenuClick(menuItemIndex)}
                          key={menuItem.label.toString()}
                          data-test={menuItem.testId}
                          id={menuItem.testId}
                        >
                          {menuItem.label}
                        </MenuItem>
                      );
                    }
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
CustomCardMenu.displayName = "CustomCardMenu";
export default CustomCardMenu;
